import { supabase } from '../supabase/client';
import { TokenTransaction, TokenTransactionType, TokenPackage, User } from '../types';
import { useEffect, useState } from 'react';
import { STRIPE_PRODUCTS } from '../stripe-config';
import { User as AuthUser } from '@supabase/supabase-js';

// Re-export TokenTransactionType for components that import it from this file
export { TokenTransactionType } from '../types';

// Helper function to add timeout to any promise
function withTimeout<T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(errorMessage));
    }, ms);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (reason) => {
        clearTimeout(timer);
        reject(reason);
      }
    );
  });
}

// Constants for the token system
export const DEFAULT_INITIAL_TOKENS = 1;
export const LOW_TOKENS_THRESHOLD = 3;
export const ADMIN_EMAIL = 'autotrindemexico@gmail.com';

// Helper function for retrying database operations
async function retryOperation<T>(
  operation: () => Promise<T>,
  retries: number = 3,
  delay: number = 2000 // 2-second delay between retries
): Promise<T> {
  for (let i = 0; i < retries; i++) {
    try {
      console.log(`[tokenService] retryOperation: Attempt ${i + 1}/${retries}`);
      return await operation();
    } catch (error) {
      console.warn(`[tokenService] retryOperation: Attempt ${i + 1}/${retries} failed:`, error);
      if (i < retries - 1) {
        console.log(`[tokenService] retryOperation: Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        console.error(`[tokenService] retryOperation: All ${retries} attempts failed, throwing error`);
        throw error; // Last retry failed, re-throw
      }
    }
  }
  throw new Error('Max retries reached'); // Should not happen if last retry throws
}

// Token packages available for purchase - now sourced from Stripe config
export const TOKEN_PACKAGES: TokenPackage[] = STRIPE_PRODUCTS.map(product => ({
  id: product.id,
  name: product.name,
  tokens: product.tokens,
  price: product.price,
  description: product.description,
  isPopular: product.isPopular,
  translationKey: product.translationKey
}));

// Check if user is admin
export const isUserAdmin = async (userId: string): Promise<boolean> => {
  if (!userId) {
    console.warn('[tokenService] isUserAdmin called without userId');
    return false;
  }

  try {
    console.log(`[tokenService] isUserAdmin: Checking admin status for user ${userId}`);
    const { data: user, error } = await supabase
      .from('users')
      .select('is_admin, email')
      .eq('id', userId);
    
    if (error || !user || user.length === 0) {
      console.log(`[tokenService] isUserAdmin: User document does not exist for ${userId} or error:`, error);
      return false;
    }
    
    const userData = user[0];
    const isAdmin = userData.is_admin === true || userData.email === ADMIN_EMAIL;
    
    console.log(`[tokenService] isUserAdmin: User ${userId} admin status: ${isAdmin}`);
    
    return isAdmin;
  } catch (error) {
    console.error('[tokenService] isUserAdmin: Error checking if user is admin:', error);
    return false;
  }
};

// Ensure admin status for user
export const ensureAdminStatus = async (userId: string, email: string): Promise<void> => {
  if (!userId || !email) {
    console.warn('[tokenService] ensureAdminStatus called with missing parameters');
    return;
  }

  try {
    if (email === ADMIN_EMAIL) {
      console.log(`[tokenService] ensureAdminStatus: Ensuring admin status for ${email} (${userId})`);
      
      const { error } = await supabase
        .from('users')
        .upsert({
          id: userId,
          email: email,
          is_admin: true
        });
      
      if (error) throw error;
      
      console.log('[tokenService] ensureAdminStatus: User updated as admin');
    }
  } catch (error) {
    console.error('[tokenService] ensureAdminStatus: Error updating admin status:', error);
    throw new Error('Error al actualizar estado de administrador');
  }
};

// Get user's token count
export const getUserTokens = async (userId: string): Promise<number> => {
  if (!userId) {
    console.warn('[tokenService] getUserTokens called without userId');
    return 0;
  }

  try {
    console.log(`[tokenService] getUserTokens: Fetching tokens for user ${userId}`);
    const { data: users, error } = await supabase
      .from('users')
      .select('tokens, is_admin, email')
      .eq('id', userId);
    
    if (error || !users || users.length === 0) {
      console.warn(`[tokenService] getUserTokens: No user document found for userId: ${userId} or error:`, error);
      return 0;
    }

    const userData = users[0];
    if (userData.is_admin === true || userData.email === ADMIN_EMAIL) {
      console.log(`[tokenService] getUserTokens: User ${userId} is admin, returning unlimited tokens`);
      return 999999;
    }
    
    // Return actual token count for regular users, ensuring we never return null
    const tokenCount = userData.tokens ?? 0;
    console.log(`[tokenService] getUserTokens: User ${userId} has ${tokenCount} tokens`);
    return tokenCount;
  } catch (error) {
    console.error('[tokenService] getUserTokens: Error getting user tokens:', error);
    // Instead of throwing, return 0 and let the UI handle the display
    return 0;
  }
};

// Initialize tokens for new user
export const initializeUserTokens = async (user: AuthUser): Promise<UserTokenInfo> => {
  try {
    // Helper function to get user with retry logic
    const getUserWithRetry = async (maxAttempts = 5, delay = 1000): Promise<any> => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const { data: existingUser, error: selectError } = await supabase
          .from('users')
          .select('email, tokens, is_admin')
          .eq('id', user.id)
          .single();

        if (!selectError) {
          return existingUser;
        }

        if (selectError.code !== 'PGRST116') {
          console.error('[tokenService] initializeUserTokens: Error checking user:', selectError);
          throw selectError;
        }

        // User not found, wait for database trigger to create the record
        if (attempt < maxAttempts) {
          console.log(`[tokenService] User record not found, waiting for trigger... (attempt ${attempt}/${maxAttempts})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      throw new Error('User record not created by database trigger after multiple attempts');
    };

    // Wait for user to be created by database trigger and get the record
    const existingUser = await getUserWithRetry();
    console.log('[tokenService] initializeUserTokens: User found:', existingUser);

    return existingUser;
  } catch (error) {
    console.error('[tokenService] initializeUserTokens: Error:', error);
    throw error;
  }
};

// Use a token (subtract 1)
export const useToken = async (userId: string, description: string = 'Cotización realizada'): Promise<boolean> => {
  if (!userId) {
    console.warn('[tokenService] useToken called without userId');
    return false;
  }

  try {
    console.log(`[tokenService] useToken: Attempting to use token for user ${userId}`);
    const isAdmin = await isUserAdmin(userId);
    if (isAdmin) {
      console.log(`[tokenService] useToken: Admin user (${userId}) using token - no deduction needed`);
      return true;
    }
    
    const { data: users, error } = await supabase
      .from('users')
      .select('tokens')
      .eq('id', userId);
    
    if (error || !users || users.length === 0) {
      console.warn(`[tokenService] useToken: No user document found for userId: ${userId} or error:`, error);
      return false;
    }
    
    const user = users[0];
    const currentTokens = user.tokens || 0;
    
    if (currentTokens <= 0) {
      console.warn(`[tokenService] useToken: User ${userId} has no tokens available`);
      return false;
    }
    
    const { error: updateError } = await supabase
      .from('users')
      .update({
        tokens: currentTokens - 1,
        last_token_update: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (updateError) throw updateError;
    
    console.log(`[tokenService] useToken: Token consumed for user ${userId}. New total: ${currentTokens - 1}`);
    await addTokenTransaction({
      userId,
      amount: -1,
      type: TokenTransactionType.USED,
      description,
      checkoutSessionId: undefined
    });
    
    return true;
  } catch (error) {
    console.error('[tokenService] useToken: Error using token:', error);
    return false;
  }
};

// Add tokens to a user
export const addTokens = async (
  userId: string, 
  amount: number, 
  type: TokenTransactionType = TokenTransactionType.ADDED,
  description: string = 'Tokens añadidos por administrador',
  adminId?: string,
  checkoutSessionId?: string
): Promise<boolean> => {
  if (!userId || amount <= 0) {
    console.warn('[tokenService] addTokens: Parámetros inválidos. userId:', userId, 'amount:', amount);
    return false;
  }

  try {
    console.log(`[tokenService] addTokens: Intentando añadir ${amount} tokens al usuario ${userId}. Tipo: ${type}, Descripción: ${description}`);
    
    // Check for duplicate purchase if checkout session ID is provided
    if (checkoutSessionId && type === TokenTransactionType.PURCHASED) {
      console.log(`[tokenService] addTokens: Verificando duplicados para checkout session: ${checkoutSessionId}`);
      
      const { data: existingTransaction, error: duplicateCheckError } = await supabase
        .from('token_transactions')
        .select('id')
        .eq('user_id', userId)
        .eq('type', TokenTransactionType.PURCHASED)
        .eq('checkout_session_id', checkoutSessionId)
        .limit(1);
      
      if (duplicateCheckError) {
        console.error('[tokenService] addTokens: Error verificando duplicados:', duplicateCheckError);
        throw duplicateCheckError;
      }
      
      if (existingTransaction && existingTransaction.length > 0) {
        console.log(`[tokenService] addTokens: Transacción duplicada detectada para checkout session ${checkoutSessionId}, omitiendo`);
        return true; // Return true as the tokens were already added successfully
      }
    }
    
    // Obtener tokens actuales
    console.log(`[tokenService] addTokens: Obteniendo tokens actuales para el usuario ${userId}...`);
    const { data: users, error: fetchError } = await supabase
      .from('users')
      .select('tokens')
      .eq('id', userId);
    
    if (fetchError) {
      console.error('[tokenService] addTokens: Error al obtener tokens actuales:', fetchError);
      throw fetchError;
    }
    if (!users || users.length === 0) {
      console.error(`[tokenService] addTokens: Usuario ${userId} no encontrado en la tabla 'users'.`);
      throw new Error(`Usuario ${userId} no encontrado.`);
    }
    
    const currentTokens = (users && users.length > 0) ? (users[0]?.tokens || 0) : 0;
    const newTokens = currentTokens + amount;
    console.log(`[tokenService] addTokens: El usuario ${userId} tiene ${currentTokens} tokens. El nuevo total será ${newTokens}.`);
    
    // Actualizar tokens del usuario
    console.log(`[tokenService] addTokens: Actualizando tokens para el usuario ${userId} a ${newTokens}...`);
    const { error: updateError } = await supabase
      .from('users')
      .update({
        tokens: newTokens,
        last_token_update: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (updateError) {
      console.error('[tokenService] addTokens: Error al actualizar tokens del usuario:', updateError);
      throw updateError;
    }
    
    console.log(`[tokenService] addTokens: Tokens actualizados exitosamente para el usuario ${userId}. Nuevo total: ${newTokens}`);
    
    // Añadir registro de transacción de tokens
    console.log(`[tokenService] addTokens: Añadiendo registro de transacción de tokens...`);
    await addTokenTransaction({
      userId,
      amount,
      type,
      description,
      createdBy: adminId || null,
      checkoutSessionId
    });
    console.log(`[tokenService] addTokens: Registro de transacción de tokens añadido exitosamente para el usuario ${userId}`);
    
    return true;
  } catch (error) {
    console.error('[tokenService] addTokens: Error capturado en la función addTokens:', error);
    // Registrar detalles específicos del error de Supabase si están disponibles
    if (error.code && error.details) {
      console.error(`[tokenService] Código de Error de Supabase: ${error.code}, Detalles: ${error.details}, Sugerencia: ${error.hint}`);
    }
    return false;
  }
};

// Record token transaction
export const addTokenTransaction = async (transaction: Omit<TokenTransaction, 'id' | 'createdAt'>): Promise<string | null> => {
  if (!transaction.userId) {
    console.warn('[tokenService] addTokenTransaction called without userId');
    return null;
  }

  try {
    console.log(`[tokenService] addTokenTransaction: Recording transaction for user ${transaction.userId}, type: ${transaction.type}, amount: ${transaction.amount}`);
    const transactionData = {
      user_id: transaction.userId,
      amount: transaction.amount,
      type: transaction.type,
      description: transaction.description || 'Sin descripción',
      created_at: new Date().toISOString(),
      created_by: transaction.createdBy || null,
      checkout_session_id: transaction.checkoutSessionId || null
    };
    
    const { data, error } = await supabase
      .from('token_transactions')
      .insert(transactionData)
      .select()
      .single();
    
    if (error) throw error;
    console.log(`[tokenService] addTokenTransaction: Transaction recorded with ID: ${data.id}`);
    return data.id;
  } catch (error) {
    console.error('[tokenService] addTokenTransaction: Error recording token transaction:', error);
    return null;
  }
};

// Get user's token transaction history
export const getUserTokenTransactions = async (userId: string, maxResults: number = 10): Promise<TokenTransaction[]> => {
  if (!userId) {
    console.warn('[tokenService] getUserTokenTransactions called without userId');
    return [];
  }
  
  try {
    console.log(`[tokenService] getUserTokenTransactions: Fetching transactions for user ${userId}`);
    const { data: transactions, error } = await supabase
      .from('token_transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(maxResults);
    
    if (error) throw error;
    
    const formattedTransactions: TokenTransaction[] = [];
    
    transactions?.forEach(data => {
      let createdAt: Date;
      try {
        createdAt = new Date(data.created_at);
      } catch (error) {
        createdAt = new Date();
      }
      
      formattedTransactions.push({
        id: data.id,
        userId: data.user_id,
        amount: data.amount,
        type: data.type,
        description: data.description || 'Sin descripción',
        createdAt: createdAt,
        createdBy: data.created_by || null,
        checkoutSessionId: data.checkout_session_id || null
      });
    });
    
    console.log(`[tokenService] getUserTokenTransactions: Found ${formattedTransactions.length} transactions for user ${userId}`);
    return formattedTransactions;
  } catch (error) {
    console.error('[tokenService] getUserTokenTransactions: Error getting transaction history:', error);
    return [];
  }
};

// Check if user has enough tokens
export const hasEnoughTokens = async (userId: string): Promise<boolean> => {
  if (!userId) {
    console.warn('[tokenService] hasEnoughTokens called without userId');
    return false;
  }

  try {
    console.log(`[tokenService] hasEnoughTokens: Checking if user ${userId} has enough tokens`);
    const isAdmin = await isUserAdmin(userId);
    console.log(`[tokenService] hasEnoughTokens for ${userId}, isAdmin: ${isAdmin}`);
    
    if (isAdmin) {
      return true;
    }
    
    const tokens = await getUserTokens(userId);
    console.log(`[tokenService] hasEnoughTokens: User ${userId} has ${tokens} tokens`);
    return tokens > 0;
  } catch (error) {
    console.error('[tokenService] hasEnoughTokens: Error checking if user has enough tokens:', error);
    return false;
  }
};

// Get all users with their token info
export const getAllUsersWithTokenInfo = async (): Promise<User[]> => {
  try {
    console.log('[tokenService] getAllUsersWithTokenInfo: Fetching all users with token info');
    const { data: usersData, error } = await supabase
      .from('users')
      .select('*');
    
    if (error) throw error;
    
    const formattedUsers: User[] = [];
    
    usersData?.forEach(userData => {
      formattedUsers.push({
        uid: userData.id,
        email: userData.email,
        displayName: userData.display_name,
        tokens: userData.tokens || 0,
        isAdmin: userData.is_admin || false,
        lastTokenUpdate: userData.last_token_update ? new Date(userData.last_token_update) : undefined
      });
    });
    
    console.log(`[tokenService] getAllUsersWithTokenInfo: Found ${formattedUsers.length} users`);
    return formattedUsers;
  } catch (error) {
    console.error('[tokenService] getAllUsersWithTokenInfo: Error getting users token info:', error);
    return [];
  }
};

// Process token package purchase via Stripe
export const purchaseTokenPackage = async (
  userId: string,
  packageId: string
): Promise<boolean> => {
  if (!userId || !packageId) {
    console.warn('[tokenService] Invalid parameters for purchaseTokenPackage');
    return false;
  }

  try {
    console.log(`[tokenService] purchaseTokenPackage: Processing purchase for user ${userId}, package ${packageId}`);
    const tokenPackage = TOKEN_PACKAGES.find(pkg => pkg.id === packageId);
    if (!tokenPackage) {
      console.warn(`[tokenService] purchaseTokenPackage: Token package not found: ${packageId}`);
      return false;
    }
    
    await addTokens(
      userId,
      tokenPackage.tokens,
      TokenTransactionType.PURCHASED,
      `Compra de ${tokenPackage.tokens} tokens - Pago con Stripe`,
      undefined, // No admin ID for purchases
      undefined // No checkout session ID for this legacy function
    );
    
    console.log(`[tokenService] purchaseTokenPackage: Tokens added for user ${userId}`);
    return true;
  } catch (error) {
    console.error('[tokenService] purchaseTokenPackage: Error processing token purchase:', error);
    return false;
  }
};

// Custom hook for token management
export function useTokenManagement(userId: string) {
  const [tokens, setTokens] = useState<number | null>(null);
  const [hasTokens, setHasTokens] = useState<boolean>(true); // Default to true to prevent flashing
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Load token data
  useEffect(() => {
    const loadTokenData = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        // Check if user is admin first - this is crucial
        console.log('[tokenService] useTokenManagement: Checking admin status for userId:', userId);
        const adminStatus = await isUserAdmin(userId);
        console.log('[tokenService] useTokenManagement: Admin status result:', adminStatus);
        setIsAdmin(adminStatus);
        
        if (adminStatus) {
          // Admins always have tokens and bypass token checks
          setTokens(999999);
          setHasTokens(true);
          console.log('[tokenService] useTokenManagement: User is admin, setting unlimited tokens');
        } else {
          // Load tokens for regular users
          const userTokens = await getUserTokens(userId);
          setTokens(userTokens);
          setHasTokens(userTokens > 0);
          console.log('[tokenService] useTokenManagement: User tokens loaded:', userTokens);
        }
      } catch (error) {
        console.error('[tokenService] useTokenManagement: Error loading token data:', error);
        // Don't change hasTokens state on error, keep previous value
      } finally {
        setLoading(false);
        console.log('[tokenService] useTokenManagement: Finished loading token data.');
      }
    };
    
    loadTokenData();
  }, [userId]);
  
  // Consume a token
  const useTokenFn = async (description: string = 'Token used'): Promise<boolean> => {
    console.log('[tokenService] useTokenManagement: useToken called, isAdmin:', isAdmin);
    if (isAdmin) {
      console.log('[tokenService] useTokenManagement: Admin user - no token consumed');
      return true; // Admins don't consume tokens
    }
    
    try {
      console.log('[tokenService] useTokenManagement: Consuming token for non-admin user');
      const success = await useToken(userId, description);
      if (success) {
        // Update local state
        setTokens((prev) => (prev !== null ? prev - 1 : 0));
        // Update hasTokens status if this was the last token
        if (tokens === 1) setHasTokens(false);
        console.log('[tokenService] useTokenManagement: Token consumed successfully.');
        return true;
      }
      console.log('[tokenService] useTokenManagement: Failed to consume token.');
      return false;
    } catch (error) {
      console.error('[tokenService] useTokenManagement: Error using token:', error);
      return false;
    }
  };
  
  // Reload tokens from the database
  const reloadTokens = async (): Promise<void> => {
    if (!userId) return;
    
    setLoading(true);
    try {
      console.log('[tokenService] useTokenManagement: Reloading tokens for user:', userId);
      const adminStatus = await isUserAdmin(userId);
      setIsAdmin(adminStatus);
      
      if (adminStatus) {
        setTokens(999999);
        setHasTokens(true);
        console.log('[tokenService] useTokenManagement: Tokens reloaded (admin).');
        return;
      }
      
      const userTokens = await getUserTokens(userId);
      setTokens(userTokens);
      setHasTokens(userTokens > 0);
      console.log('[tokenService] useTokenManagement: Tokens reloaded (non-admin):', userTokens);
    } catch (error) {
      console.error('[tokenService] useTokenManagement: Error reloading tokens:', error);
    } finally {
      setLoading(false);
      console.log('[tokenService] useTokenManagement: Finished reloading tokens.');
    }
  };
  
  return {
    tokens,
    hasTokens,
    loading,
    isAdmin,
    useToken: useTokenFn,
    reloadTokens
  };
}